/**
 * Нативные функции для мобильного приложения
 * Автор: AI Market Hub
 * Описание: Интеграция с камерой, галереей, файловой системой, push-уведомлениями
 * 
 * ВАЖНО: Все плагины загружаются динамически для предотвращения краша
 */

import { Capacitor } from '@capacitor/core';

// ===== ОПРЕДЕЛЕНИЕ ПЛАТФОРМЫ =====

/**
 * Проверяет, запущено ли в нативном приложении (Android/iOS)
 */
export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Получает текущую платформу
 * @returns {string} 'android' | 'ios' | 'web'
 */
export const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

// ===== БЕЗОПАСНАЯ ЗАГРУЗКА ПЛАГИНОВ =====

/**
 * Безопасно загружает плагин
 */
const safeLoadPlugin = async (pluginName, importFn) => {
  try {
    const module = await importFn();
    return module;
  } catch (error) {
    console.warn(`[Native] Плагин ${pluginName} недоступен:`, error.message);
    return null;
  }
};

// ===== ИНФОРМАЦИЯ ОБ УСТРОЙСТВЕ =====

/**
 * Получает информацию об устройстве
 */
export const getDeviceInfo = async () => {
  try {
    const Device = await safeLoadPlugin('Device', () => import('@capacitor/device').then(m => m.Device));
    if (!Device) {
      return { model: 'unknown', platform: 'web', isNative: false };
    }
    
    const info = await Device.getInfo();
    const battery = await Device.getBatteryInfo().catch(() => ({ batteryLevel: 1, isCharging: false }));
    
    return {
      ...info,
      battery,
      isNative: isNative(),
      platform: getPlatform(),
    };
  } catch (error) {
    console.warn('[Native] Ошибка получения информации об устройстве:', error);
    return {
      model: 'unknown',
      platform: 'web',
      isNative: false,
    };
  }
};

/**
 * Проверяет, может ли устройство запускать локальную транскрибацию
 * Поддерживает WebGPU и WASM на ПК и мобильных устройствах!
 */
export const canRunLocalTranscription = async () => {
  try {
    // Проверяем WASM (работает везде: на ПК и в мобильных WebView)
    let hasWASM = false;
    let hasSIMD = false;
    try {
      hasWASM = typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
      if (hasWASM) {
        hasSIMD = WebAssembly.validate(new Uint8Array([
          0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
          0x01, 0x05, 0x01, 0x60, 0x00, 0x01, 0x7b, 0x03,
          0x02, 0x01, 0x00, 0x0a, 0x0a, 0x01, 0x08, 0x00,
          0x41, 0x00, 0xfd, 0x0f, 0xfd, 0x62, 0x0b
        ]));
      }
    } catch {
      hasWASM = false;
    }
    
    // На мобильном приложении
    if (isNative()) {
      const deviceInfo = await getDeviceInfo();
      const battery = deviceInfo.battery || { batteryLevel: 1, isCharging: false };
      const hasSufficientBattery = battery.batteryLevel >= 0.3 || battery.isCharging;
      
      // Проверяем память устройства (нужно минимум 3GB для tiny модели)
      const deviceRAM = navigator.deviceMemory || 4;
      const hasEnoughRAM = deviceRAM >= 3;
      
      if (!hasEnoughRAM) {
        return {
          canRun: false,
          backend: null,
          hasSufficientBattery,
          useServer: true,
          reason: '📱 Недостаточно памяти (нужно 3+ GB)',
          deviceRAM,
          platform: 'mobile',
        };
      }
      
      if (!hasSufficientBattery) {
        return {
          canRun: false,
          backend: null,
          hasSufficientBattery,
          useServer: true,
          reason: '🔋 Низкий заряд батареи (< 30%)',
          platform: 'mobile',
        };
      }
      
      if (hasWASM) {
        return {
          canRun: true,
          backend: 'wasm',
          hasSufficientBattery,
          useServer: false,
          reason: hasSIMD ? '📱 WASM+SIMD на устройстве (быстро)' : '📱 WASM на устройстве',
          speed: hasSIMD ? 'medium' : 'slow',
          simd: hasSIMD,
          platform: 'mobile',
        };
      }
      
      return {
        canRun: false,
        backend: null,
        hasSufficientBattery,
        useServer: true,
        reason: '📱 WASM не поддерживается на этом устройстве',
        platform: 'mobile',
      };
    }
    
    // На веб-платформе (ПК/браузер)
    const hasEnoughRAM = typeof navigator !== 'undefined' && navigator.deviceMemory 
      ? navigator.deviceMemory >= 2 
      : true;
    
    if (!hasEnoughRAM) {
      return {
        canRun: false,
        backend: null,
        reason: '💻 Недостаточно оперативной памяти',
        useServer: true,
        platform: 'web',
      };
    }
    
    // Проверяем WebGPU (только на ПК, в мобильных WebView редко поддерживается)
    let hasWebGPU = false;
    if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
      try {
        const adapter = await navigator.gpu.requestAdapter();
        hasWebGPU = !!adapter;
      } catch {
        hasWebGPU = false;
      }
    }
    
    if (hasWebGPU) {
      return {
        canRun: true,
        backend: 'webgpu',
        reason: '🎮 WebGPU доступен (GPU-ускорение)',
        useServer: false,
        speed: 'fast',
        platform: 'web',
      };
    }
    
    if (hasWASM) {
      return {
        canRun: true,
        backend: 'wasm',
        reason: hasSIMD ? '💻 WASM+SIMD доступен (CPU-ускорение)' : '💻 WASM доступен (CPU)',
        useServer: false,
        speed: hasSIMD ? 'medium' : 'slow',
        simd: hasSIMD,
        platform: 'web',
      };
    }
    
    // Ничего не поддерживается
    return {
      canRun: false,
      backend: null,
      reason: 'Устройство не поддерживает локальную транскрибацию',
      useServer: true,
      platform: 'web',
    };
  } catch (error) {
    console.warn('[Native] Ошибка проверки возможностей:', error);
    return {
      canRun: false,
      backend: null,
      useServer: true,
      reason: 'Ошибка определения возможностей устройства',
    };
  }
};

// ===== РАБОТА С МЕДИА =====

/**
 * Выбор видео из галереи
 */
export const pickVideoFromGallery = async () => {
  try {
    if (!isNative()) {
      // Для веб используем стандартный input
      return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'video/*';
        input.onchange = (e) => {
          const file = e.target.files?.[0];
          if (file) {
            resolve({ file, path: URL.createObjectURL(file) });
          } else {
            reject(new Error('Файл не выбран'));
          }
        };
        input.click();
      });
    }
    
    // Для нативного используем Camera API
    const Camera = await safeLoadPlugin('Camera', () => import('@capacitor/camera').then(m => m.Camera));
    if (!Camera) {
      throw new Error('Camera плагин недоступен');
    }
    
    const result = await Camera.pickImages({
      quality: 100,
      limit: 1,
    });
    
    if (result.photos && result.photos.length > 0) {
      return {
        path: result.photos[0].path || result.photos[0].webPath,
        webPath: result.photos[0].webPath,
      };
    }
    
    throw new Error('Видео не выбрано');
  } catch (error) {
    console.error('[Native] Ошибка выбора видео:', error);
    throw error;
  }
};

// ===== ПОДЕЛИТЬСЯ =====

/**
 * Поделиться контентом
 */
export const shareContent = async (title, text, url = null) => {
  try {
    const Share = await safeLoadPlugin('Share', () => import('@capacitor/share').then(m => m.Share));
    if (!Share) {
      // Fallback для веб
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return true;
      }
      return false;
    }
    
    await Share.share({
      title,
      text,
      url,
      dialogTitle: 'Поделиться',
    });
    
    return true;
  } catch (error) {
    console.warn('[Native] Ошибка шаринга:', error);
    return false;
  }
};

/**
 * Копировать в буфер обмена
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    await hapticFeedback();
    return true;
  } catch (error) {
    console.warn('[Native] Ошибка копирования:', error);
    return false;
  }
};

// ===== ОБРАТНАЯ СВЯЗЬ =====

/**
 * Вибрация при нажатии
 */
export const hapticFeedback = async (style = 'light') => {
  try {
    if (!isNative()) return;
    
    const Haptics = await safeLoadPlugin('Haptics', () => import('@capacitor/haptics').then(m => m.Haptics));
    if (!Haptics) return;
    
    const { ImpactStyle } = await import('@capacitor/haptics');
    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    
    await Haptics.impact({ style: styleMap[style] || ImpactStyle.Light });
  } catch (error) {
    // Игнорируем ошибки вибрации
  }
};

// ===== СТАТУС БАР =====

/**
 * Настройка статус бара
 */
export const setupStatusBar = async () => {
  try {
    if (!isNative()) return;
    
    const StatusBar = await safeLoadPlugin('StatusBar', () => import('@capacitor/status-bar').then(m => m.StatusBar));
    if (!StatusBar) return;
    
    const { Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0f172a' });
  } catch (error) {
    console.warn('[Native] Ошибка настройки статус бара:', error);
  }
};

// ===== PUSH УВЕДОМЛЕНИЯ =====

/**
 * Инициализация push-уведомлений
 */
export const initPushNotifications = async () => {
  try {
    if (!isNative()) return null;
    
    const PushNotifications = await safeLoadPlugin('PushNotifications', () => import('@capacitor/push-notifications').then(m => m.PushNotifications));
    if (!PushNotifications) return null;
    
    // Запрашиваем разрешение
    const permission = await PushNotifications.requestPermissions();
    
    if (permission.receive !== 'granted') {
      console.warn('[Native] Push-уведомления не разрешены');
      return null;
    }
    
    // Регистрируемся
    await PushNotifications.register();
    
    return null;
  } catch (error) {
    console.error('[Native] Ошибка инициализации push:', error);
    return null;
  }
};

// ===== ЖИЗНЕННЫЙ ЦИКЛ ПРИЛОЖЕНИЯ =====

/**
 * Инициализация нативных функций при запуске
 */
export const initNativeApp = async () => {
  console.log('[Native] Инициализация...', {
    isNative: isNative(),
    platform: getPlatform(),
  });
  
  try {
    // Настройка статус бара
    await setupStatusBar();
    
    // Push уведомления (не блокируем если не работает)
    initPushNotifications().catch(() => {});
    
    console.log('[Native] Инициализация завершена');
  } catch (error) {
    console.warn('[Native] Ошибка при инициализации:', error);
  }
};

// ===== СЕТЕВОЙ СТАТУС =====

/**
 * Проверяет наличие интернет-соединения
 */
export const checkNetworkStatus = async () => {
  try {
    const Network = await safeLoadPlugin('Network', () => import('@capacitor/network').then(m => m.Network));
    if (!Network) {
      return { connected: true, connectionType: 'unknown' };
    }
    
    const status = await Network.getStatus();
    return {
      connected: status.connected,
      connectionType: status.connectionType,
    };
  } catch (error) {
    return { connected: true, connectionType: 'unknown' };
  }
};
